<template>
  <div class="favourites-sidebar" :class="{ active: favouritesSidebarActive }">
    <div class="favourite-sidebar__content">
      <button class="sidebar__close" @click="toggleFavSidebar">
        <img src="@/assets/close.svg" alt="" />
      </button>

      <h3 class="favourite-sidebar__heading">
        {{ $t('favourites.findFlat') }}
      </h3>

      <Query class="favourite-sidebar__query"></Query>
      <h3 class="favourite-sidebar__heading">{{ $t('favourites.sortBy') }}</h3>
      <Dropdown :list="dropdownList" @selected="changeProperty"></Dropdown>

      <div class="favourite-sidebar__sorting">
        <button class="favourite-sidebar__button" @click="toggleOrder">
          <span v-if="sorting.order == -1">
            {{ $t('favourites.ascending') }}</span
          >
          <span v-else> {{ $t('favourites.descending') }}</span>
        </button>
      </div>

      <h3 class="favourite-sidebar__heading">{{ $t('favourites.flatsIn') }}</h3>
      <div class="favourite-sidebar__circle">
        <vue-circle
          ref="circle"
          :progress="countProgress(flatsQuantity, max)"
          :size="140"
          :reverse="false"
          line-cap="round"
          :fill="{ color: colors.available }"
          :empty-fill="colors.lighterGray"
          :animation-start-value="0.0"
          :start-angle="Math.PI / 2"
          insert-mode="append"
          :thickness="6"
          :show-percent="false"
        >
          <p class="favourite-sidebar__inner-circle">
            {{ flatsQuantity }}/{{ this.max }}
          </p>
        </vue-circle>
      </div>

      <div class="favourite-sidebar__progressbars">
        <h3 class="favourite-sidebar__heading">
          {{ getStatus(flatProperties['status_available']) }}
        </h3>
        <vue-progress
          class="favourite-sidebar__progress"
          :percent="countProgress(availableFlatsQuantity, flatsQuantity)"
          :color="colors.available"
          :bg-color="colors.lighterGray"
          :show-text="true"
          :format="() => `${availableFlatsQuantity}/${flatsQuantity}`"
        ></vue-progress>

        <h3 class="favourite-sidebar__heading">
          {{ getStatus(flatProperties['status_reserved']) }}
        </h3>
        <vue-progress
          class="favourite-sidebar__progress"
          :percent="countProgress(reservedFlatsQuantity, flatsQuantity)"
          :color="colors.reserved"
          :bg-color="colors.lighterGray"
          :show-text="true"
          :format="() => `${reservedFlatsQuantity}/${flatsQuantity}`"
        ></vue-progress>

        <h3 class="favourite-sidebar__heading">
          {{ getStatus(flatProperties['status_sold']) }}
        </h3>
        <vue-progress
          class="favourite-sidebar__progress"
          :percent="countProgress(soldFlatsQuantity, flatsQuantity)"
          :color="colors.sold"
          :bg-color="colors.lighterGray"
          :show-text="true"
          :format="() => `${soldFlatsQuantity}/${flatsQuantity}`"
        ></vue-progress>
      </div>

      <button class="favourite-sidebar__delete" @click="clearFavourites">
        <Icon :medium="true"><img src="@/assets/bin.png" alt=""/></Icon>
        <span>{{ $t('favourites.delete') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import VueCircle from 'vue2-circle-progress/src/index.vue';
import VueProgress from 'k-progress';
import Dropdown from '@/components/form/dropdown';
import Icon from '@/components/icon';
import Query from '@/components/favourites/favourites-query';
import colors from '@/styles/_variables.scss';

import { mapMutations, mapState, mapGetters } from 'vuex';

export default {
  name: 'favourites-sidebar',
  components: {
    Dropdown,
    Query,
    VueCircle,
    VueProgress,
    Icon,
  },
  props: {
    flats: { type: Array, required: true },
  },
  data() {
    return {
      colors: colors,
      max: 99,
    };
  },
  computed: {
    ...mapState({
      sorting: state => state.browser.sorting,
      flatProperties: state => state.browser.flatProperties,
      favouritesSidebarActive: state => state.browser.favouritesSidebarActive,
    }),
    ...mapGetters({ getStatus: 'GET_STATUS_NAME' }),
    flatsQuantity() {
      return this.flats.length;
    },
    availableFlatsQuantity() {
      return this.flats.filter(
        el =>
          this.getStatus(el[this.flatProperties['status']]) ==
          this.getStatus(this.flatProperties['status_available'])
      ).length;
    },
    reservedFlatsQuantity() {
      return this.flats.filter(
        el =>
          this.getStatus(el[this.flatProperties['status']]) ==
          this.getStatus(this.flatProperties['status_reserved'])
      ).length;
    },
    soldFlatsQuantity() {
      return this.flats.filter(el => {
        return (
          this.getStatus(el[this.flatProperties['status']]) ==
          this.getStatus(this.flatProperties['status_sold'])
        );
      }).length;
    },
    dropdownList() {
      return [
        {
          value: this.flatProperties['area'],
          text: this.$t('singleFlat.area'),
        },
        {
          value: this.flatProperties['rooms'],
          text: this.$t('singleFlat.rooms'),
        },
        {
          value: this.flatProperties['floor'],
          text: this.$t('singleFlat.floor'),
        },
      ];
    },
  },
  watch: {
    flatsQuantity(value) {
      this.$refs.circle.updateProgress(this.countProgress(value, this.max));
    },
  },
  methods: {
    ...mapMutations({
      changeOrder: 'SET_SORTING_ORDER',
      changeProperty: 'SET_SORTING_PROPERTY',
      clearFavourites: 'CLEAR_FAVOURITES',
      toggleFavSidebar: 'TOGGLE_FAV_SIDEBAR',
    }),
    countProgress(current, max) {
      if (max == 0) return 0;
      return (current / max) * 100;
    },
    toggleOrder() {
      this.changeOrder(this.sorting.order * -1);
    },
  },
};
</script>

<style lang="scss">
$favourites-sidebarWidth: 350px;
.favourites-sidebar {
  position: relative;
  z-index: 20;

  display: flex;
  flex-direction: column;
  align-items: center;
  width: $favourites-sidebarWidth;
  min-width: $favourites-sidebarWidth;
  flex-basis: $favourites-sidebarWidth;
  overflow-x: hidden;

  padding: 2%;
  padding-top: $topbarHeight + 10px;
  background: #fff;
  box-shadow: 0 0 40px $lightGray;

  transition: 250ms;

  @media screen and (max-width: $xs) {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 35;
    max-width: 100%;
    width: 100%;
    height: 100%;
    padding: {
      top: $topbarHeight + 10px;
      bottom: 40px;
      left: 8%;
      right: 8%;
    }
    //overflow: hidden;
    justify-content: space-around;
    transform: translateX(-105%);

    &.active {
      transform: translateX(0);
    }
  }
  @media screen and (max-height: 575px) {
    display: none;
  }
}

.favourite-sidebar__content {
  position: fixed;

  width: inherit;

  padding: {
    left: 2%;
    right: 2%;
  }

  @media screen and (max-width: $xs) {
    position: static;
  }
}

.favourite-sidebar__heading {
  font-weight: 300;
  font-size: 16px;
  margin-bottom: 5%;
}

.favourite-sidebar__query {
  margin-bottom: 5%;
}

.favourite-sidebar__sorting {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}

.favourite-sidebar__icon {
  margin-right: 6px;
  margin-bottom: 6px;
}

.favourite-sidebar__button {
  border: 1px solid $lightGray;
  background: #fff;
  border-radius: 8px;
  padding: 10px 11px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  margin-right: 6px;
  margin-bottom: 6px;
  transition: 200ms;

  &:hover,
  &.active {
    border: 1px solid $primaryColor;
  }
}

.favourite-sidebar__circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 5%;
}

.favourite-sidebar__inner-circle {
  font-size: 1.6rem;
}

.favourite-sidebar__progressbars {
  @media screen and (max-width: $xs) {
    display: none;
  }
  @media screen and (max-height: 850px) {
    display: none;
  }
}

.favourite-sidebar__progress {
  width: 100%;
  margin-bottom: 3%;
}

.k-progress-outer {
  margin: 0;
  padding: 0;
}

.k-progress-text {
  min-width: 32px;
  margin-left: 6px;
  text-align: right;
}

.favourite-sidebar__delete {
  display: flex;

  align-items: center;

  font-size: 14px;
  background: $lighterGray;
  border: none;
  color: #000;
  padding: 16px 26px;
  margin: {
    top: 8%;
    left: auto;
    right: auto;
  }
  border-radius: 24px;
  cursor: pointer;

  transition: background 200ms;

  &:hover {
    background: darken($lighterGray, 2%);
  }

  span {
    margin-left: 6px;
  }
}

.sidebar__close {
  display: none;
  justify-content: center;
  align-items: center;

  width: 40px;
  height: 40px;

  background: none;
  border: 1px solid #000;

  border-radius: 50%;

  margin-bottom: 1rem;
  margin-left: auto;

  cursor: pointer;

  img {
    display: block;
  }

  @media screen and (max-width: $xs) {
    display: flex;
  }
}
</style>
